import { useEffect, useRef } from 'preact/hooks';

import type { TemplateFunction, TemplateResult } from 'Components/types';
import { searchRequestSelector, totalHitsSelector } from 'Core/selectors/search';
import { viewSelector } from 'Core/selectors/show';
import { useSelector } from 'Core/store';
import type {
  default as SearchItemData,
  SearchItemTemplateParams as BaseSearchItemTemplateParams,
} from 'Models/searchItemData';
import { track } from 'Modules/serverApi/index';
import { cloneSafe } from 'Utils/components';
import { observeVisibility } from 'Utils/intersectionObserver';
import safeTemplateCall from 'Utils/safeTemplateCall';

import ComparerCheckBox from './comparerCheckBox';

interface Props {
  template: TemplateFunction<SearchItemTemplateParams>;
  item: SearchItemData;
}

export type SearchItemTemplateParams = BaseSearchItemTemplateParams<{
  comparerCheckBox: TemplateResult;
  position: number;
  view: string;
}>;

export default function SearchItem({ template, item }: Props): TemplateResult {
  const view = useSelector(viewSelector);
  const searchTotalHits = useSelector(totalHitsSelector);
  const searchRequest = useSelector(searchRequestSelector);

  const rootRef = useRef<HTMLElement | null>(null);

  const isTargetInItemLink = (target: EventTarget | null) => {
    const linkTarget = target instanceof Element ? target : null;
    const link = linkTarget?.closest<HTMLAnchorElement>('a[href]:not([data-cm-prevent-redirect])');
    if (link) {
      const { origin } = window.location;

      const targetUrl = new URL(link.href, origin);
      targetUrl.hash = '';

      const itemUrl = new URL(item.url as string, origin);

      return targetUrl.href === itemUrl.href;
    }
    return false;
  };

  const position = item.position ?? -1;

  useEffect(() => {
    const element = rootRef.current;

    if (!item.id || !element) {
      return;
    }

    const detach = observeVisibility(element, () => {
      track('ProductImpression', {
        item,
        position,
        searchRequest,
      });
    });

    return () => {
      detach();
    };
  }, [position, searchRequest?.pageNumber, searchRequest?.pageSize, searchRequest, item]);

  const comparerCheckBox = <ComparerCheckBox item={item} />;

  const component = safeTemplateCall(
    template,
    { ...item.params, comparerCheckBox, view, position },
    'SearchItem',
    item.id as string,
  );
  return cloneSafe(component, rootRef, {
    onMouseUp: (e) => {
      if ((e.button === 0 || e.button === 1) && isTargetInItemLink(e.target)) {
        track('ClickOnSearchResult', { item, position, searchTotalHits });
      }
    },
  });
}
